import React, { useState, useRef, useEffect } from "react";
import { FaEye, FaRegEyeSlash } from "react-icons/fa";
import styles from "./AdharRegistrartion.module.scss";
import {
  getUserByAadhar,
  verifyGetByAadhar,
  getUserDetails,
} from "../../../../store/slices/abhaRegistrationSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  MAX_OTP_RESEND_ATTEMPTS,
  getOtpResendAttemptMessage,
} from "./otpResendAttempts";

const AadharVerification = ({
  clinicId,
  clinicName,
  setAadharNumber,
  setShowRegistrationModal,
  setShowAdharRegistration,
  setModalVisible,
  loading,
}) => {
  const consentTexts = [
    `I am voluntarily sharing my Aadhaar Number / Virtual ID issued by UIDAI, and my demographic information for the purpose of creating an Ayushman Bharat Health Account number (“ABHA number”) and address (“ABHA Address”). I authorize NHA to use my Aadhaar number / VID for authentication with UIDAI as per the Aadhaar Act, 2016. I understand UIDAI will share my e-KYC details or a “Yes” response with NHA upon successful authentication.`,
    `I intend to create ABHA number and ABHA Address using a document other than Aadhaar. (Click here to proceed further)`,
    `I consent to usage of my ABHA address and number for linking of my legacy (past) government health records and those generated during this encounter.`,
    `I authorize the sharing of all my health records with healthcare provider(s) for the purpose of providing healthcare services during this encounter.`,
    `I consent to the anonymization and subsequent use of my government health records for public health purposes.`,
    `I, (name of healthcare worker), confirm that I have duly informed and explained the beneficiary about this consent form.`,
    `I, (beneficiary name), have been explained the above and hereby provide my consent.`,
  ];
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const [aadhaarPart1, setAadhaarPart1] = useState("");
  const [aadhaarPart2, setAadhaarPart2] = useState("");
  const [aadhaarPart3, setAadhaarPart3] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [aadhaarError, setAadhaarError] = useState("");
  const [showAadharField, setShowAadharField] = useState(true);
  const [adharOtpGenerateMsg, setAdharOtpGenerateMsg] = useState("");
  const [showOtpField, setShowOtpField] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [adhaarForThisPage, setAadhaarForThisPage] = useState("");
  const inputRefs = useRef([]);
  const [redirectToRegister, setRedirectToRegister] = useState(false);
  const [timer, setTimer] = useState(20);
  const OTP_RESEND_TIMEOUT = 60;
  const [resendCount, setResendCount] = useState(0);
  const [maxAttemptsReached, setMaxAttemptsReached] = useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const [checkedState, setCheckedState] = useState(
    new Array(consentTexts.length).fill(false)
  );
  const [beneficiaryName, setBeneficiaryName] = useState("");
  const [beneficiaryNameError, setBeneficiaryNameError] = useState("");

  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);

  useEffect(() => {
    let interval;
    if (showOtpField && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [showOtpField, timer]);

  const formatTimer = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleInputChange = (e, setValue, length, nextRef, prevRef) => {
    const value = e.target.value.replace(/\D/g, "");
    setValue(value);
    if (aadhaarError) setAadhaarError("");
    if (value.length === length && nextRef) nextRef.current.focus();
    else if (value.length === 0 && prevRef) prevRef.current.focus();
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleCheckboxChange = (index) => {
    const updatedCheckedState = checkedState?.map((item, idx) =>
      idx === index ? !item : item
    );
    setCheckedState(updatedCheckedState);
    setShowConsentError("");
  };

  const handleNextClick = async () => {
    const fullAadhaar = aadhaarPart1 + aadhaarPart2 + aadhaarPart3;
    setAadharNumber(fullAadhaar);
    setAadhaarForThisPage(fullAadhaar);

    if (!/^\d{12}$/.test(fullAadhaar)) {
      setAadhaarError("Please enter a valid 12-digit Aadhaar number.");
      return;
    }

    const anyUnchecked = checkedState?.some(
      (isChecked, idx) => idx !== 1 && !isChecked
    );
    if (anyUnchecked) {
      setShowConsentError(true);
      return;
    }
    setShowConsentError(false);

    if (!beneficiaryName.trim()) {
      setBeneficiaryNameError("Please enter the beneficiary name.");
      return;
    }
    setBeneficiaryNameError("");

    try {
      const response = await dispatch(
        getUserByAadhar({ clinicId, aadharNumber: fullAadhaar, token })
      ).unwrap();

      if (response?.success && response?.result?.list) {
        setAdharOtpGenerateMsg(response?.result?.list);
        setShowAadharField(false);
        setShowOtpField(true);
        setTimer(OTP_RESEND_TIMEOUT);
      } else {
        setAadhaarError("Invalid Aadhaar number. Please try again.");
      }
    } catch (err) {
      setAadhaarError("Something went wrong. Please try again.");
    }
  };

  const handleOtpChange = (e, index) => {
    setOtpError("");
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleResendOtp = async () => {
    if (timer > 0 || resendCount >= MAX_OTP_RESEND_ATTEMPTS) return;

    try {
      setOtpError("");
      const response = await dispatch(
        getUserByAadhar({
          clinicId,
          aadharNumber: adhaarForThisPage,
          token,
        })
      ).unwrap();
      if (response?.success) {
        setOtp(["", "", "", "", "", ""]);
        setTimer(OTP_RESEND_TIMEOUT);
        const nextCount = resendCount + 1;
        setResendCount(nextCount);
        if (nextCount >= MAX_OTP_RESEND_ATTEMPTS) {
          setMaxAttemptsReached(true);
        }
        return;
      }
      setOtpError("Failed to resend OTP. Please try again.");
    } catch (err) {
      console.error("Error during OTP resend:", err);
      setOtpError("Failed to resend OTP. Please try again.");
    }
  };

  const handleFinalNextClick = async () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6 || !enteredOtp) {
      setOtpError("Please enter a valid 6-digit OTP.");
      return;
    }
    setOtpError("");

    try {
      const response = await dispatch(
        verifyGetByAadhar({ clinicId, otpValue: enteredOtp, token })
      );
      if (
        response?.payload?.success === true &&
        response?.payload?.result?.list?.authResult === "success"
      ) {
        const userDetailsResponse = await dispatch(
          getUserDetails({ clinicId: clinicId, token })
        );
        if (userDetailsResponse?.payload?.success) {
          setOtpError("");
          setShowRegistrationModal(false);
          setModalVisible(true);
        } else {
          setOtpError("Something went wrong. Please try again");
        }
      } else if (response?.payload?.success === false) {
        setShowOtpField(false);
        setRedirectToRegister(true);
        return;
      }
      setOtpError("Invalid OTP. Please try again.");
    } catch (err) {
      setOtpError("Something went wrong. Please try again.");
    }
  };

  const handleGoToPatientRegistration = () => {
    setShowAdharRegistration(true);
  };

  return (
    <div>
      {showAadharField && (
        <div>
          <div className={`d-flex gap-3 align-items-end ${styles.consent}`}>
            <div className="form-group" style={{ width: "22%" }}>
              <label className="col-form-label">Aadhaar Number :</label>
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                value={aadhaarPart1}
                ref={ref1}
                onChange={(e) =>
                  handleInputChange(e, setAadhaarPart1, 4, ref2, null)
                }
                maxLength={4}
                placeholder="0000"
              />
            </div>

            <div className="form-group" style={{ width: "25%" }}>
              <label
                className="col-form-label"
                style={{ visibility: "hidden" }}
              >
                Aadhaar Number
              </label>
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                value={aadhaarPart2}
                ref={ref2}
                onChange={(e) =>
                  handleInputChange(e, setAadhaarPart2, 4, ref3, ref1)
                }
                maxLength={4}
                placeholder="0000"
              />
            </div>

            <div className="form-group" style={{ width: "25%" }}>
              <label
                className="col-form-label"
                style={{ visibility: "hidden" }}
              >
                Aadhaar Number
              </label>
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                value={aadhaarPart3}
                ref={ref3}
                onChange={(e) =>
                  handleInputChange(e, setAadhaarPart3, 4, null, ref2)
                }
                maxLength={4}
                placeholder="0000"
              />
            </div>

            <div
              className="form-group"
              style={{ width: "5%", marginBottom: "1.5rem", cursor: "pointer" }}
            >
              <label
                className="col-form-label"
                style={{ visibility: "hidden" }}
              >
                Show
              </label>
              {showPassword ? (
                <FaEye
                  style={{ fontSize: "1.5rem" }}
                  onClick={togglePasswordVisibility}
                />
              ) : (
                <FaRegEyeSlash
                  style={{ fontSize: "1.5rem" }}
                  onClick={togglePasswordVisibility}
                />
              )}
            </div>
          </div>

          {aadhaarError && (
            <p style={{ color: "red", fontSize: "0.8rem" }}>{aadhaarError}</p>
          )}

          <div
            className="border p-3 rounded"
            style={{ backgroundColor: "#f8f9fa" }}
          >
            <p className="fw-bold mb-3 small">I hereby declare that:</p>

            {consentTexts?.map((text, index) => {
              const isChildConsent = index >= consentTexts?.length - 2;
              const isLastConsent = index === consentTexts?.length - 1;
              const updatedText = text?.replace(
                "(name of healthcare worker)",
                `<strong>${clinicName}</strong>`
              );

              return (
                <div
                  key={index}
                  className={`d-flex align-items-start mb-2 small lh-sm ${
                    isChildConsent ? "ms-4" : ""
                  }`}
                >
                  <input
                    className="form-check-input mt-1 me-2 flex-shrink-0
"
                    type="checkbox"
                    id={`consent-${index}`}
                    checked={checkedState[index]}
                    onChange={() => handleCheckboxChange(index)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`consent-${index}`}
                  >
                    {isLastConsent && text?.includes("(beneficiary name)") ? (
                      (() => {
                        const parts = text?.split("(beneficiary name)");
                        return (
                          <>
                            {parts[0]}
                            <input
                              type="text"
                              placeholder="Enter beneficiary name"
                              value={beneficiaryName}
                              onChange={(e) => {
                                setBeneficiaryName(e.target.value);
                                setBeneficiaryNameError("");
                              }}
                              className="mx-1 px-1 py-0 fs-6"
                              required
                              aria-invalid={!!beneficiaryNameError}
                            />
                            {parts[1]}
                          </>
                        );
                      })()
                    ) : (
                      <span dangerouslySetInnerHTML={{ __html: updatedText }} />
                    )}
                  </label>
                </div>
              );
            })}
            {showConsentError && (
              <div className="text-danger mt-3">
                Please accept all consents to proceed.
              </div>
            )}
            {beneficiaryNameError && (
              <div className="text-danger mt-2">{beneficiaryNameError}</div>
            )}
          </div>

          <div className="text-end w-100">
            <button
              className="btn btn-primary"
              onClick={handleNextClick}
              disabled={loading}
            >
              {loading ? "Processing..." : "Next"}
            </button>
          </div>
        </div>
      )}

      {showOtpField && (
        <div className={styles.adhaarDiv}>
          <p>Confirm OTP</p>
          <label className="col-form-label">{adharOtpGenerateMsg}</label>

          <div className="d-flex gap-2 mt-2">
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                className="form-control text-center"
                value={digit}
                onChange={(e) => handleOtpChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                maxLength={1}
                ref={(el) => (inputRefs.current[index] = el)}
                style={{ width: "4rem" }}
              />
            ))}
          </div>

          {otpError && <p className="text-danger small mt-2">{otpError}</p>}
          {resendCount > 0 && (
            <p
              className={`small mt-2 mb-0 ${
                resendCount >= MAX_OTP_RESEND_ATTEMPTS
                  ? "text-danger"
                  : "text-success"
              }`}
            >
              {getOtpResendAttemptMessage(resendCount)}
            </p>
          )}

          <div className="mt-2">
            {!maxAttemptsReached ? (
              <>
                <small className="fs-13">Didn't receive the OTP?</small>{" "}
                <small
                  onClick={handleResendOtp}
                  style={{
                    cursor: timer > 0 ? "not-allowed" : "pointer",
                    color: timer > 0 ? "gray" : "blue",
                    marginLeft: "0.5rem",
                  }}
                >
                  Resend OTP
                </small>
                <span className="ms-3">Time left: {formatTimer(timer)}</span>
              </>
            ) : (
              <div className="text-black fs-13">
                Resend OTP limit reached. Please try again later.
              </div>
            )}
          </div>

          <div className="text-end mt-3">
            <button
              className="btn btn-success"
              onClick={handleFinalNextClick}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Verifying...
                </>
              ) : (
                "Verify OTP"
              )}
            </button>
          </div>
        </div>
      )}

      {redirectToRegister && (
        <div>
          <h2 className="text-xl font-semibold text-center text-red-600 mb-4">
            No ABHA user registered with this Aadhaar number.
          </h2>
          <div className="flex justify-center">
            <button
              onClick={handleGoToPatientRegistration}
              className="btn btn-primary px-4 py-2 mx-auto d-block"
            >
              Register in ABHA
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AadharVerification;
